import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, User, ArrowRight, Gift } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const [mode, setMode] = useState(refCode ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error de autenticacion");
      }
      const data = await res.json();
      login(data);
      toast.success(mode === "login" ? "Bienvenido de vuelta" : "Cuenta creada exitosamente");
      // Auto-apply referral code if present and user just registered
      if (mode === "register" && refCode) {
        try {
          await fetch(`${API}/referrals/apply`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ referral_code: refCode }),
            credentials: "include",
          });
          toast.success("Codigo de referido aplicado - 10% de descuento");
        } catch {}
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="auth-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#193B6E] relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-40 h-40 bg-[#20D3C0] rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 right-16 w-56 h-56 bg-[#20D3C0] rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 text-center px-12">
          <img
            src="https://customer-assets.emergentagent.com/job_examen-prep-mx/artifacts/0mkzmukl_Logo%20Mi%20Prepa%20en%20Shorts.jpeg"
            alt="Mi Prepa en Shorts"
            className="w-32 h-32 rounded-2xl mx-auto mb-8 shadow-2xl"
          />
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            Mi Prepa en Shorts
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed max-w-md">
            Acredita tu bachillerato mexicano con nuestros programas de preparacion para Exacer-COLBACH y Acredita-Bach
          </p>
        </div>
      </div>

      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_examen-prep-mx/artifacts/0mkzmukl_Logo%20Mi%20Prepa%20en%20Shorts.jpeg"
              alt="Mi Prepa en Shorts"
              className="w-16 h-16 rounded-xl mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-[#193B6E]" style={{ fontFamily: 'Outfit' }}>Mi Prepa en Shorts</h1>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgba(25,59,110,0.08)] border border-slate-100">
            {refCode && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="referral-banner">
                <Gift className="w-5 h-5 text-green-500 shrink-0" />
                <p className="text-sm text-green-700">
                  Registrate con el codigo <strong className="font-mono">{refCode}</strong> y obtiene <strong>10% de descuento</strong>
                </p>
              </div>
            )}
            <h2 className="text-2xl font-bold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="auth-title">
              {mode === "login" ? "Iniciar Sesion" : "Crear Cuenta"}
            </h2>
            <p className="text-slate-500 mb-6 text-sm">
              {mode === "login" ? "Ingresa a tu cuenta para continuar" : "Registrate para comenzar tu preparacion"}
            </p>

            {/* Google Login Button */}
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 rounded-xl px-4 py-3 font-medium text-[#193B6E] hover:bg-slate-50 transition-colors mb-6"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continuar con Google
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-sm text-slate-400">o</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <Label htmlFor="name" className="text-[#193B6E] text-sm font-medium">Nombre completo</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Tu nombre"
                      className="pl-10"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-[#193B6E] text-sm font-medium">Correo electronico</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@correo.com"
                    className="pl-10"
                    required
                    data-testid="auth-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-[#193B6E] text-sm font-medium">Contrasena</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contrasena"
                    className="pl-10"
                    required
                    data-testid="auth-password-input"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-xl px-4 py-3 transition-all hover:-translate-y-0.5 shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-[#112C54] border-t-transparent rounded-full"></div>
                ) : (
                  <>
                    {mode === "login" ? "Iniciar Sesion" : "Crear Cuenta"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-500">
              {mode === "login" ? "No tienes cuenta?" : "Ya tienes cuenta?"}{" "}
              <button
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="text-[#20D3C0] font-semibold hover:underline"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? "Registrate" : "Inicia sesion"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
